export function DataDeletion() {
  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold text-stone-900 dark:text-white mb-2">Data Deletion Policy</h1>
      <p className="text-sm text-stone-500 dark:text-zinc-500 mb-8">Last updated: March 18, 2026</p>

      <div className="prose prose-stone dark:prose-invert prose-sm max-w-none space-y-6">
        <section>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed">
            SocialCast respects your right to control your data. This page explains how to request deletion of your data and what happens when you do.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">How to Delete Your Data</h2>
          <div className="space-y-4">
            <div className="p-4 bg-stone-50 dark:bg-zinc-800/50 rounded-xl border border-stone-200/60 dark:border-zinc-700/60">
              <h3 className="font-semibold text-stone-800 dark:text-zinc-200 mb-1">Disconnect a Social Media Account</h3>
              <p className="text-stone-600 dark:text-zinc-400 text-sm">
                Go to <strong>Accounts</strong> in SocialCast and click "Disconnect" on any account. This immediately revokes our access and deletes the associated OAuth tokens and credentials.
              </p>
            </div>

            <div className="p-4 bg-stone-50 dark:bg-zinc-800/50 rounded-xl border border-stone-200/60 dark:border-zinc-700/60">
              <h3 className="font-semibold text-stone-800 dark:text-zinc-200 mb-1">Delete Your SocialCast Account</h3>
              <p className="text-stone-600 dark:text-zinc-400 text-sm">
                Contact us at <a href="mailto:privacy@socialcast.dev" className="text-orange-600 dark:text-orange-400 hover:underline">privacy@socialcast.dev</a> to request full account deletion. We will delete all your data within 30 days, including:
              </p>
              <ul className="list-disc pl-5 text-stone-600 dark:text-zinc-400 text-sm mt-2 space-y-0.5">
                <li>Your account profile and credentials</li>
                <li>All connected social media account data and OAuth tokens</li>
                <li>Post history, drafts, and scheduled posts</li>
                <li>Uploaded media files</li>
                <li>Analytics data</li>
                <li>Inbox messages and comments</li>
              </ul>
            </div>

            <div className="p-4 bg-stone-50 dark:bg-zinc-800/50 rounded-xl border border-stone-200/60 dark:border-zinc-700/60">
              <h3 className="font-semibold text-stone-800 dark:text-zinc-200 mb-1">Facebook / Instagram Data Deletion</h3>
              <p className="text-stone-600 dark:text-zinc-400 text-sm">
                If you signed up or connected via Facebook or Instagram, you can also request data deletion through your Facebook settings under <strong>Settings &gt; Apps and Websites &gt; SocialCast &gt; Remove</strong>. This triggers automatic deletion of all Facebook/Instagram-related data we store.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">What We Delete</h2>
          <ul className="list-disc pl-5 text-stone-600 dark:text-zinc-400 space-y-1">
            <li>OAuth access tokens and refresh tokens (encrypted credentials)</li>
            <li>Social media profile information (username, avatar, account metadata)</li>
            <li>Post content created through SocialCast</li>
            <li>Media files uploaded to our storage</li>
            <li>Analytics and engagement data</li>
            <li>Inbox data (comments, replies, messages)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">What We Cannot Delete</h2>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed">
            Content already published to social media platforms (tweets, Instagram posts, Facebook posts, etc.) lives on those platforms and is not affected by deleting your SocialCast data. To delete published content, use the respective platform's tools or the delete function in SocialCast before disconnecting.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">Timeline</h2>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed">
            Data deletion is processed within 30 days of your request. You will receive a confirmation email once deletion is complete.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">Contact</h2>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed">
            For data deletion requests or questions, email <a href="mailto:privacy@socialcast.dev" className="text-orange-600 dark:text-orange-400 hover:underline">privacy@socialcast.dev</a>.
          </p>
        </section>
      </div>
    </div>
  );
}
